/**
 * Duration and clock formatting, in one place.
 *
 * Every one of these is rendered in IBM Plex Mono with tabular figures and a
 * fixed column width, because a timer whose glyphs change width makes the whole
 * table shimmer once a second — which on a board people watch for six hours is
 * not a cosmetic problem.
 */

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

const pad = (n: number): string => (n < 10 ? `0${String(n)}` : String(n));

/**
 * A compact elapsed time. Four widths, each stable within its band:
 *   `0:07` `12:34` `2h 04m` `3d 05h`
 */
export function fmtDuration(ms: number): string {
  const clamped = Math.max(0, ms);
  if (clamped < HOUR) {
    const total = Math.floor(clamped / SECOND);
    return `${String(Math.floor(total / 60))}:${pad(total % 60)}`;
  }
  if (clamped < DAY) {
    const hours = Math.floor(clamped / HOUR);
    return `${String(hours)}h ${pad(Math.floor((clamped % HOUR) / MINUTE))}m`;
  }
  const days = Math.floor(clamped / DAY);
  return `${String(days)}d ${pad(Math.floor((clamped % DAY) / HOUR))}h`;
}

/** 24-hour wall clock. Staff read these against each other, not against AM/PM. */
export function fmtClock(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '--:--';
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Whether two timestamps fall on the same local day — the transcript's day rule. */
export function isSameDay(a: string, b: string): boolean {
  const x = new Date(a);
  const y = new Date(b);
  return (
    x.getFullYear() === y.getFullYear() &&
    x.getMonth() === y.getMonth() &&
    x.getDate() === y.getDate()
  );
}

/* -------------------------------------------------------------------------- */
/* The WhatsApp 24-hour service window                                         */
/* -------------------------------------------------------------------------- */

export const SERVICE_WINDOW_MS = 24 * HOUR;

/** Amber at five minutes, red at fifteen. The queue is judged on these. */
export const WAIT_WARN_MS = 5 * MINUTE;
export const WAIT_LATE_MS = 15 * MINUTE;

/** The window is measured from the candidate's last inbound message, not ours. */
export function windowExpiresAt(lastInboundAt: string | null): number | null {
  if (lastInboundAt === null) return null;
  const at = new Date(lastInboundAt).getTime();
  return Number.isNaN(at) ? null : at + SERVICE_WINDOW_MS;
}

export type WindowStatus = 'open' | 'closing' | 'expired' | 'never-opened';

/**
 * `closing` starts at two hours left: that is roughly the point at which a
 * reply that needs a colleague's input will not make it, and the person holding
 * the conversation has to decide now whether to send something or let it fall
 * to a template.
 */
export function windowStatus(expiresAt: number | null, now: number): WindowStatus {
  if (expiresAt === null) return 'never-opened';
  const left = expiresAt - now;
  if (left <= 0) return 'expired';
  if (left <= 2 * HOUR) return 'closing';
  return 'open';
}
